import {
  createEmptyDraftForm,
  getBuilderSchema,
  listFieldTemplates,
  saveBuilderSchema,
  savePlanSymbols,
  type FieldTemplate,
  type SaveBuilderSchemaPayload,
} from "@/lib/wartungen";
import { DEFAULT_ENABLED_GRADES, normalizeEnabledGrades } from "@/pages/wartungen/form/runtimeModel";
import {
  fieldToSavePayload,
  normalizeField,
  semanticSortOrderByKey,
  type BuilderField,
} from "@/pages/wartungen/kategorieEinstellungen/fieldModel";
import { normalizeBehaviorForSave } from "@/pages/wartungen/kategorieEinstellungen/answerRules";

export const FORM_EXPORT_VERSION = "maintenance-form-export.v1" as const;

export interface MaintenanceFormExportField extends Record<string, unknown> {
  field_key: string;
  template_key?: string | null;
  template_id?: number | null;
}

export interface MaintenanceFormExport {
  export_version: typeof FORM_EXPORT_VERSION;
  exported_at: string;
  source: {
    maintenance_type_key?: string;
    form_id?: string | number;
    form_name?: string;
  };
  grade_config_json: { enabled_grades: number[] };
  fields: MaintenanceFormExportField[];
  plan_symbols?: Record<string, unknown>[];
}

function slugifyExportName(name: string): string {
  const base = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "");
  return base || "formular";
}

function fieldToPortableExportPayload(
  field: BuilderField,
  formId: string,
  sortOrder: number,
  templateKeyById: Map<number, string>,
): MaintenanceFormExportField {
  const payload = fieldToSavePayload(field, formId, sortOrder);
  payload.behavior_override_json = normalizeBehaviorForSave(payload.behavior_override_json);
  const templateKey =
    field.template_id != null ? templateKeyById.get(field.template_id) ?? null : null;
  return {
    ...payload,
    field_key: field.field_key,
    template_key: templateKey,
  } as MaintenanceFormExportField;
}

function buildTemplateKeyMap(templates: FieldTemplate[]): Map<number, string> {
  const map = new Map<number, string>();
  for (const template of templates) {
    const id = Number(template.id);
    const key = String(template.template_key || "").trim();
    if (Number.isFinite(id) && id > 0 && key) {
      map.set(id, key);
    }
  }
  return map;
}

function buildTemplateIdByKey(templates: FieldTemplate[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const template of templates) {
    const id = Number(template.id);
    const key = String(template.template_key || "").trim();
    if (Number.isFinite(id) && id > 0 && key && !map.has(key)) {
      map.set(key, id);
    }
  }
  return map;
}

function parseExportDocument(raw: unknown): MaintenanceFormExport {
  if (!raw || typeof raw !== "object") {
    throw new Error("Die Datei enthält kein gültiges JSON-Objekt.");
  }
  const doc = raw as Partial<MaintenanceFormExport>;
  if (doc.export_version !== FORM_EXPORT_VERSION) {
    throw new Error(
      `Unbekannte Export-Version: ${String(doc.export_version ?? "—")}. Erwartet: ${FORM_EXPORT_VERSION}.`,
    );
  }
  if (!Array.isArray(doc.fields)) {
    throw new Error("Das Export-Format enthält kein gültiges fields-Array.");
  }
  const fields = doc.fields.filter((field) => {
    const key = String(field?.field_key ?? "").trim();
    return key.length > 0;
  }) as MaintenanceFormExportField[];
  if (!fields.length) {
    throw new Error("Das Export-Format enthält keine Felder.");
  }
  return {
    export_version: FORM_EXPORT_VERSION,
    exported_at: String(doc.exported_at || new Date().toISOString()),
    source: doc.source ?? {},
    grade_config_json: {
      enabled_grades: normalizeEnabledGrades(doc.grade_config_json),
    },
    fields,
    plan_symbols: Array.isArray(doc.plan_symbols) ? doc.plan_symbols : undefined,
  };
}

export async function exportMaintenanceForm(options: {
  formId: string | number;
  formName: string;
  categoryKey?: string;
}): Promise<void> {
  const formId = String(options.formId).trim();
  if (!formId) throw new Error("form_id fehlt für den Export.");

  const [schema, templates] = await Promise.all([
    getBuilderSchema(formId),
    listFieldTemplates().catch(() => [] as FieldTemplate[]),
  ]);
  const formName = String(
    options.formName || schema.form_name || schema.name || `Formular ${formId}`,
  ).trim();
  const rawFields = Array.isArray(schema.fields) ? schema.fields : [];
  const builderFields = rawFields.map((field) => normalizeField(field, formId, formName));
  const templateKeyById = buildTemplateKeyMap(templates);
  const order = semanticSortOrderByKey(builderFields);
  const fields = builderFields.map((field) =>
    fieldToPortableExportPayload(field, formId, order.get(field.field_key) ?? -1, templateKeyById),
  );

  const payload: MaintenanceFormExport = {
    export_version: FORM_EXPORT_VERSION,
    exported_at: new Date().toISOString(),
    source: {
      maintenance_type_key: options.categoryKey,
      form_id: formId,
      form_name: formName,
    },
    grade_config_json: {
      enabled_grades: normalizeEnabledGrades(schema.grade_config_json),
    },
    fields,
    plan_symbols: Array.isArray(schema.plan_symbols)
      ? (schema.plan_symbols as Record<string, unknown>[])
      : undefined,
  };

  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${slugifyExportName(formName)}-${formId}.json`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export interface ImportMaintenanceFormResult {
  formId: string;
  formName: string;
  fieldCount: number;
  importedPlanSymbols: boolean;
  missingTemplateKeys: string[];
}

export async function importMaintenanceForm(options: {
  categoryKey: string;
  file: File;
  formName?: string;
  importPlanSymbols?: boolean;
}): Promise<ImportMaintenanceFormResult> {
  const categoryKey = options.categoryKey.trim();
  if (!categoryKey) throw new Error("Kategorie fehlt für den Import.");

  const text = await options.file.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("Die Datei ist kein gültiges JSON.");
  }

  const doc = parseExportDocument(parsed);
  const defaultName =
    options.formName?.trim() ||
    String(doc.source.form_name || "Importiertes Formular").trim() ||
    "Importiertes Formular";
  const importName = defaultName.endsWith(" (Import)")
    ? defaultName
    : `${defaultName} (Import)`;

  const templates = await listFieldTemplates().catch(() => [] as FieldTemplate[]);
  const templateIdByKey = buildTemplateIdByKey(templates);
  const missingTemplateKeys = new Set<string>();

  const { form_id: newFormId } = await createEmptyDraftForm(categoryKey, importName);
  const targetFormId = String(newFormId).trim();
  if (!targetFormId) throw new Error("Neues Formular wurde erstellt, aber ohne form_id.");

  const saveFields = doc.fields.map((field, index) => {
    const next = { ...field };
    delete next.template_key;

    const templateKey = String(field.template_key ?? "").trim();
    if (templateKey) {
      const resolvedId = templateIdByKey.get(templateKey);
      if (resolvedId != null) {
        next.template_id = resolvedId;
      } else {
        missingTemplateKeys.add(templateKey);
      }
    }

    next.form_id = targetFormId;
    if (next.sort_order == null) next.sort_order = index;
    return next;
  });

  const savePayload: SaveBuilderSchemaPayload = {
    fields: saveFields,
    deleted_field_keys: [],
    renamed_field_keys: [],
    grade_config_json: doc.grade_config_json,
  };
  await saveBuilderSchema(targetFormId, savePayload);

  let importedPlanSymbols = false;
  if (options.importPlanSymbols !== false && doc.plan_symbols?.length) {
    await savePlanSymbols(categoryKey, doc.plan_symbols);
    importedPlanSymbols = true;
  }

  return {
    formId: targetFormId,
    formName: importName,
    fieldCount: saveFields.length,
    importedPlanSymbols,
    missingTemplateKeys: [...missingTemplateKeys].sort(),
  };
}

export function isMaintenanceFormExportFileName(name: string): boolean {
  return name.toLowerCase().endsWith(".json");
}

export { DEFAULT_ENABLED_GRADES };
